import React from 'react';
import moment from 'moment';
import { T, NUMERIC, money } from '../customer/customers.theme';
import { CHROME, EMPHASIS, niceCeiling, shortMoney } from './dashboard.theme';

/**
 * Revenue, one column per day.
 *
 * A single measure over time, so there is one colour and no legend: the quiet
 * grey is context, the brand orange is the day being talked about, and the
 * darkest step marks the best day in view. Only those two are labelled — a
 * number on every column is chaos and goes unread; the rest are a hover away.
 *
 * The chart measures its container rather than scaling a fixed viewBox, so the
 * type stays the same size as the type around it and nothing spills out of the
 * card at any window width.
 */

const PAD_LEFT = 46;
const PAD_RIGHT = 6;
const PLOT_TOP = 22;
const AXIS_BAND = 26;

function useWidth() {
  const ref = React.useRef(null);
  const [width, setWidth] = React.useState(0);

  React.useEffect(() => {
    const measure = () => {
      if (ref.current) setWidth(ref.current.offsetWidth);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  return [ref, width];
}

export default function RevenueTrend({ days, currency, todayKey, height = 212 }) {
  const [ref, width] = useWidth();
  const [hover, setHover] = React.useState(null);

  const plotHeight = height - PLOT_TOP - AXIS_BAND;
  const baseline = PLOT_TOP + plotHeight;

  const max = days.reduce((top, day) => Math.max(top, day.total), 0);
  const ceiling = niceCeiling(max);
  const peakKey = max > 0 ? (days.find((day) => day.total === max) || {}).date : null;

  const plotWidth = Math.max(0, width - PAD_LEFT - PAD_RIGHT);
  const slot = days.length ? plotWidth / days.length : 0;
  const barWidth = Math.max(4, Math.min(24, slot * 0.62));

  const xOf = (index) => PAD_LEFT + slot * index + (slot - barWidth) / 2;
  const heightOf = (value) => (ceiling > 0 ? Math.round((value / ceiling) * plotHeight) : 0);

  // At most seven ticks, always including the last day.
  const tickEvery = Math.max(1, Math.ceil(days.length / 7));

  const hovered = hover !== null ? days[hover] : null;

  return (
    <div ref={ref} style={{ position: 'relative', width: '100%' }}>
      {width > 0 ? (
        <svg width={width} height={height} style={{ display: 'block' }}>
          {/* gridlines: solid hairlines, one step off the surface */}
          <line x1={PAD_LEFT} y1={PLOT_TOP} x2={width - PAD_RIGHT} y2={PLOT_TOP} stroke={CHROME.grid} strokeWidth="1" />
          <line
            x1={PAD_LEFT}
            y1={PLOT_TOP + plotHeight / 2}
            x2={width - PAD_RIGHT}
            y2={PLOT_TOP + plotHeight / 2}
            stroke={CHROME.grid}
            strokeWidth="1"
          />
          <line x1={PAD_LEFT} y1={baseline} x2={width - PAD_RIGHT} y2={baseline} stroke={CHROME.axis} strokeWidth="1" />

          <text x={PAD_LEFT - 8} y={PLOT_TOP + 4} textAnchor="end" fontSize="10" fill={CHROME.label}>
            {shortMoney(ceiling, currency)}
          </text>
          <text x={PAD_LEFT - 8} y={PLOT_TOP + plotHeight / 2 + 4} textAnchor="end" fontSize="10" fill={CHROME.label}>
            {shortMoney(ceiling / 2, currency)}
          </text>
          <text x={PAD_LEFT - 8} y={baseline + 4} textAnchor="end" fontSize="10" fill={CHROME.label}>
            {shortMoney(0, currency)}
          </text>

          {days.map((day, index) => {
            const barHeight = heightOf(day.total);
            const x = xOf(index);
            const y = baseline - barHeight;
            const isToday = day.date === todayKey;
            const isPeak = !isToday && day.date === peakKey && max > 0;
            const fill = isToday ? EMPHASIS.accent : isPeak ? EMPHASIS.peak : EMPHASIS.context;

            return (
              <g key={day.date}>
                {barHeight > 0 ? (
                  <g>
                    {/* 4px rounded cap, square where it meets the baseline */}
                    <rect x={x} y={y} width={barWidth} height={barHeight} rx="4" fill={fill} />
                    {barHeight > 6 ? (
                      <rect x={x} y={baseline - 5} width={barWidth} height="5" fill={fill} />
                    ) : null}
                  </g>
                ) : null}

                {/* the hit area is the whole column, not the bar */}
                <rect
                  x={PAD_LEFT + slot * index}
                  y={PLOT_TOP}
                  width={slot}
                  height={plotHeight}
                  fill="transparent"
                  onMouseEnter={() => setHover(index)}
                  onMouseLeave={() => setHover(null)}
                />
              </g>
            );
          })}

          {/* label only what the chart is about */}
          {days.map((day, index) => {
            const isToday = day.date === todayKey;
            const isPeak = !isToday && day.date === peakKey && max > 0;
            if ((!isToday && !isPeak) || !day.total) return null;
            return (
              <text
                key={`label-${day.date}`}
                x={Math.min(width - PAD_RIGHT, Math.max(PAD_LEFT, xOf(index) + barWidth / 2))}
                y={baseline - heightOf(day.total) - 7}
                textAnchor="middle"
                fontSize="11"
                fontWeight="700"
                fill={isToday ? T.ink : EMPHASIS.peak}
              >
                {shortMoney(day.total, currency)}
              </text>
            );
          })}

          {days.map((day, index) => {
            const isLast = index === days.length - 1;
            if (!isLast && index % tickEvery !== 0) return null;
            const isToday = day.date === todayKey;
            return (
              <text
                key={`tick-${day.date}`}
                x={xOf(index) + barWidth / 2}
                y={baseline + 18}
                textAnchor="middle"
                fontSize="10"
                fontWeight={isToday ? 700 : 400}
                fill={isToday ? T.ink : CHROME.label}
              >
                {isToday ? 'Today' : moment(day.date).format('D MMM')}
              </text>
            );
          })}
        </svg>
      ) : (
        <div style={{ height }} />
      )}

      {hovered ? (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: Math.min(
              Math.max(0, xOf(hover) + barWidth / 2 - 64),
              Math.max(0, width - 128)
            ),
            width: 128,
            padding: '8px 10px',
            background: T.surface,
            border: `1px solid ${T.borderStrong}`,
            borderRadius: T.radiusControl,
            boxShadow: T.dialog,
            pointerEvents: 'none',
            boxSizing: 'border-box',
          }}
        >
          <div style={{ fontSize: 11, color: T.inkFaint }}>
            {moment(hovered.date).format('ddd D MMM')}
          </div>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: T.ink, ...NUMERIC }}>
            {money(hovered.total, currency)}
          </div>
          <div style={{ fontSize: 11, color: T.inkFaint, ...NUMERIC }}>
            {hovered.orders} {hovered.orders === 1 ? 'order' : 'orders'}
          </div>
        </div>
      ) : null}
    </div>
  );
}
